// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/api/client";
import { useStoreDetailQuery } from "@/api/hooks/use-stores";
import type { components } from "@/api/schema";
import { type StoreSchedule, storeScheduleSchema } from "./types";

// Seeded for stores that don't have a schedule row yet (the API returns no `schedule`).
const DEFAULT_SCHEDULE: StoreSchedule = {
  base: { type: "every_hour", labelType: "model", awakeTime: 1 },
  exceptions: [],
};

export function useStoreScheduleQuery(storeName: string) {
  const detail = useStoreDetailQuery(storeName);
  // Memoize on the (cache-stable) detail data so the parsed object keeps a stable reference —
  // otherwise a fresh parse each render retriggers the consumers' effects in a loop.
  const data = useMemo<StoreSchedule | undefined>(() => {
    if (!detail.data) return undefined;
    const parsed = storeScheduleSchema.safeParse(detail.data.schedule);
    return parsed.success ? parsed.data : DEFAULT_SCHEDULE;
  }, [detail.data]);
  return {
    data,
    isLoading: detail.isLoading,
    isError: detail.isError,
    error: detail.error,
  };
}

export function useUpdateStoreScheduleMutation(storeName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (schedule: StoreSchedule) => {
      const body: components["schemas"]["StoresEditRequest"] = { schedule };
      const res = await api.PATCH("/v1/stores/{store_name}", {
        params: { path: { store_name: storeName } },
        body,
      });
      if (res.error) throw new Error("Failed to update schedule");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores", "detail", storeName] });
    },
  });
}
